<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[9999999999] overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9999]"
          @click="closeDialog"
        ></div>

        <!-- Dialog panel -->
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:p-0 relative z-[10000]"
        >
          <div
            class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
          >
            <!-- Close button -->
            <button
              @click="closeDialog"
              class="absolute right-4 top-4 rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none"
            >
              <span class="sr-only">Close</span>
              <svg
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <!-- Video container -->
            <div class="relative aspect-video w-full">
              <template v-if="isYouTube">
                <iframe
                  :src="youtubeEmbedUrl"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  class="w-full h-full rounded-lg"
                ></iframe>
              </template>
              <template v-else>
                <video
                  ref="videoRef"
                  class="h-full w-full"
                  controls
                  preload="metadata"
                  :src="videoUrl"
                  :poster="posterUrl"
                >
                  <source :src="videoUrl" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </template>
            </div>

            <!-- Content section -->
            <div class="px-6 py-4">
              <h3
                class="text-xl font-semibold leading-6 text-gray-900"
                id="modal-title"
              >
                {{ title }}
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">{{ description }}</p>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="bg-gray-50/10 px-6 py-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                class="inline-flex w-full justify-center rounded-md bg-green px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto"
                @click="closeDialog"
              >
                {{ closeButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";

interface Props {
  show: boolean;
  videoUrl: string;
  title?: string;
  description?: string;
  posterUrl?: string;
  closeButtonText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Welcome to Our Platform",
  description:
    "Watch this short video to learn how to get started with our platform.",
  closeButtonText: "Got it",
  posterUrl: "",
});

const emit = defineEmits(["close", "update:show"]);

const videoRef = ref<HTMLVideoElement | null>(null);

const closeDialog = () => {
  emit("close");
  if (videoRef.value) {
    videoRef.value.pause();
  }
};

const isYouTube = computed(() => {
  const url = props.videoUrl || "";
  return url.includes("youtube.com") || url.includes("youtu.be");
});

const youtubeEmbedUrl = computed(() => {
  if (!props.videoUrl) return "";
  // Extract video ID and build embed URL
  const match = props.videoUrl.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([\w-]{11})/,
  );
  const videoId = match ? match[1] : "";
  return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : "";
});

watch(
  () => props.show,
  (val) => {
    if (!val && videoRef.value) {
      videoRef.value.pause();
    }
  },
);

onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.pause();
  }
});
</script>

<style scoped>
.aspect-video {
  aspect-ratio: 16 / 9;
}
</style>
